import type { ServiceItemInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, Select } from 'antd';
import { useEffect } from 'react';
import { IntervalFields } from '../../../components/IntervalFields';
import type { TruckClassOption } from '../hooks/use-truck-class-options';
import type { ServiceItemView } from '../service-items.api';

interface ServiceItemFormDrawerProps {
  open: boolean;
  /** Present to edit that item; absent to create a new one. */
  editing?: ServiceItemView;
  /** The truck classes to pick from; `null` while they load. */
  classes: TruckClassOption[] | null;
  error: string | null;
  submitting: boolean;
  onSubmit: (input: ServiceItemInput) => Promise<boolean>;
  onClose: () => void;
}

const EMPTY: Partial<ServiceItemInput> = { code: '', name: '', truckClassCodes: [], intervalKm: null, intervalDays: null, intervalHours: null };

/** One drawer for create and edit: the item's own interval is the default every class line without one of its own uses. */
export function ServiceItemFormDrawer({ open, editing, classes, error, submitting, onSubmit, onClose }: ServiceItemFormDrawerProps) {
  const [form] = Form.useForm<ServiceItemInput>();

  useEffect(() => {
    if (open) form.setFieldsValue(editing ?? EMPTY);
  }, [open, editing, form]);

  async function submit(values: ServiceItemInput) {
    const hasInterval = values.intervalKm != null || values.intervalDays != null || values.intervalHours != null;
    if (!hasInterval) {
      form.setFields([{ name: 'intervalKm', errors: ['Give at least one interval: km, days or hours'] }]);
      return;
    }
    if (await onSubmit(values)) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? 'Edit service item' : 'New service item'} placement="right" size={420} destroyOnHidden>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<ServiceItemInput> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="truckClassCodes"
          label="Applies to truck classes"
          extra="Every truck of these classes is serviced for this item. You can leave one out for a single truck on its own page."
        >
          <Select mode="multiple" placeholder="Select or search truck classes" options={classes ?? []} loading={classes === null} optionFilterProp="label" />
        </Form.Item>
        <IntervalFields hint="Give at least one. Whichever comes first makes the item due." />
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Create service item'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
